import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../services/localeHelpers';
import {
  Image as ImageIcon, Pause, Play, RotateCcw, Loader2, AlertCircle,
  CheckCircle2, Clock, Camera, Zap, BarChart3, Settings2, RefreshCw,
} from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../services/firebaseService';
import i18n from '../../../i18n';

interface ImageScanStatus {
  lastRunAt: string | null;
  lastCompletedAt: string | null;
  lastRunDurationMs: number | null;
  lastRunProcessed: number;
  lastRunFound: number;
  lastRunSkipped: number;
  lastRunRemaining: number;
  lastRunSources: Record<string, number>;
  totalProcessed: number;
  totalFound: number;
  totalSkipped: number;
  bySpotType: Record<string, { candidates: number }>;
  lastRunWaypointsFound: number;
  lastRunWaypointsSkipped: number;
  lastRunRoutesUpdated: number;
  consecutiveEmptyRuns: number;
  lastError: string | null;
  lastErrorAt: string | null;
  paused: boolean;
  resetRequestedAt: string | null;
  resetInProgress: boolean;
  lastResetAt: string | null;
  lastResetCount: number;
}

interface ImageStats {
  totalWithAutoImage: number;
  totalAttempted: number;
  totalScanned: number;
  bySource: Record<string, number>;
  byAccuracy: Record<string, number>;
}

const SPOT_TYPE_LABELS: Record<string, string> = {
  confirmed: 'admin.imagescan.type_verified',
  rasteplass: 'admin.imagescan.type_rest_area',
  osm_amenity: 'OSM-anlegg',
  commercial_campsite: 'admin.imagescan.type_campsite',
  coastal: 'admin.imagescan.type_coastal',
  user_created: 'admin.imagescan.type_user_created',
};

function formatDate(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return d.toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '–';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return i18n.t('admin.imagescan.just_now');
  if (mins < 60) return i18n.t('admin.imagescan.mins_ago', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return i18n.t('admin.imagescan.hours_ago', { hours, mins: mins % 60 });
  const days = Math.floor(hours / 24);
  return i18n.t('admin.imagescan.days_ago', { count: days });
}

function formatDuration(ms: number | null): string {
  if (!ms) return '–';
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('nb-NO');
}

const AdminImageScanDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ImageScanStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ImageStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [backfillResult, setBackfillResult] = useState<any>(null);

  // Real-time listener on system/image_scan_status
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'system', 'image_scan_status'), (snap) => {
      if (snap.exists()) setStatus(snap.data() as ImageScanStatus);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Load image stats on mount
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const fn = httpsCallable(functions, 'getAutoImageStats');
      const r = await fn();
      setStats(r.data as ImageStats);
    } catch { /* ignore */ }
    setStatsLoading(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleAction = async (action: 'pause' | 'resume' | 'reset') => {
    setActionLoading(action);
    try {
      const fn = httpsCallable(functions, 'resetImageScan');
      await fn({ action });
      if (action === 'reset') setConfirmReset(false);
    } catch (err: any) {
      console.error('Action failed:', err);
    }
    setActionLoading(null);
  };

  const handleManualBackfill = async () => {
    setActionLoading('backfill');
    setBackfillResult(null);
    try {
      const fn = httpsCallable(functions, 'backfillLocationImages', { timeout: 600000 });
      const r = await fn({ batchSize: 50 });
      setBackfillResult(r.data);
      // Refresh stats after backfill
      loadStats();
    } catch (err: any) {
      setBackfillResult({ error: err.message });
    }
    setActionLoading(null);
  };

  const handleRestartScan = async () => {
    setActionLoading('restart');
    setBackfillResult(null);
    try {
      // 1. Reset attempted flags
      const resetFn = httpsCallable(functions, 'resetImageScan');
      await resetFn({ action: 'reset' });
      // 2. Make sure scanning is not paused
      await resetFn({ action: 'resume' });
      // 3. Trigger immediate batch
      const backfillFn = httpsCallable(functions, 'backfillLocationImages', { timeout: 600000 });
      const r = await backfillFn({ batchSize: 50 });
      setBackfillResult(r.data);
      loadStats();
    } catch (err: any) {
      setBackfillResult({ error: err.message });
    }
    setActionLoading(null);
  };

  // Compute health status
  const minutesSinceLastRun = status?.lastRunAt
    ? (Date.now() - new Date(status.lastRunAt).getTime()) / 60000
    : Infinity;

  const isComplete = (status?.consecutiveEmptyRuns ?? 0) > 0 && (status?.lastRunRemaining ?? -1) === 0;
  const isPaused = status?.paused === true;
  const isStale = !isPaused && !isComplete && minutesSinceLastRun > 20;
  const hasError = !!status?.lastError;
  const isResetting = status?.resetInProgress === true;
  const isResetPending = !!status?.resetRequestedAt && !isResetting;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
        <p className="text-xs text-[var(--text-muted)] font-medium tracking-wide uppercase">Laster bildeskann-status...</p>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="p-6 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl flex items-start gap-4">
        <AlertCircle className="text-[var(--text-muted)] shrink-0" size={18} />
        <div>
          <p className="text-sm font-bold text-[var(--text-primary)]">Ingen data ennå</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Venter på første kjøring av scheduledImageBackfill (hvert 10. minutt).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">

      {/* ── Status Banner ── */}
      <div className={`p-4 rounded-2xl border flex items-center gap-3 ${
        isResetting || isResetPending
          ? 'bg-amber-500/10 border-amber-500/20'
          : hasError
            ? 'bg-red-500/10 border-red-500/20'
            : isStale
              ? 'bg-orange-500/10 border-orange-500/20'
              : isPaused
                ? 'bg-amber-500/10 border-amber-500/20'
                : isComplete
                  ? 'bg-emerald-500/10 border-emerald-500/20'
                  : 'bg-emerald-500/10 border-emerald-500/20'
      }`}>
        <div className="flex-1 flex items-center gap-3">
          {isResetting ? (
            <>
              <Loader2 size={16} className="animate-spin text-amber-400" />
              <span className="text-sm font-bold text-amber-400">Nullstilling pågår — fjerner attempted-flagg...</span>
            </>
          ) : isResetPending ? (
            <>
              <Clock size={16} className="text-amber-400" />
              <span className="text-sm font-bold text-amber-400">Nullstilling venter — utføres ved neste schedulerte kjøring</span>
            </>
          ) : hasError ? (
            <>
              <AlertCircle size={16} className="text-red-400" />
              <div>
                <span className="text-sm font-bold text-red-400">Siste kjøring feilet</span>
                <p className="text-xs text-red-400/70 mt-0.5">{status.lastError} ({formatRelativeTime(status.lastErrorAt)})</p>
              </div>
            </>
          ) : isStale ? (
            <>
              <AlertCircle size={16} className="text-orange-400" />
              <div>
                <span className="text-sm font-bold text-orange-400">Mulig stoppet</span>
                <p className="text-xs text-orange-400/70 mt-0.5">Siste kjøring var {formatRelativeTime(status.lastRunAt)} — scheduler kan ha stoppet etter deploy</p>
              </div>
            </>
          ) : isPaused ? (
            <>
              <Pause size={16} className="text-amber-400" />
              <span className="text-sm font-bold text-amber-400">Skanning er pauset av admin</span>
            </>
          ) : isComplete ? (
            <>
              <CheckCircle2 size={16} className="text-emerald-400" />
              <span className="text-sm font-bold text-emerald-400">Ferdig — alle synlige steder er skannet</span>
            </>
          ) : (
            <>
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-bold text-emerald-400">Aktiv — siste kjøring {formatRelativeTime(status.lastRunAt)}</span>
            </>
          )}
        </div>

        {/* Quick-action button in banner when scan is not actively running */}
        {(isComplete || isPaused || isStale || hasError) && !isResetting && !isResetPending && (
          <button
            onClick={isComplete ? handleRestartScan : isPaused ? () => handleAction('resume') : handleManualBackfill}
            disabled={actionLoading !== null}
            className={`shrink-0 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all text-white ${
              actionLoading ? 'opacity-50 cursor-wait' : ''
            } ${isComplete ? 'bg-violet-600 hover:bg-violet-500' : isPaused ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-cyan-600 hover:bg-cyan-500'}`}
          >
            {actionLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : isComplete ? (
              <RefreshCw size={12} />
            ) : isPaused ? (
              <Play size={12} />
            ) : (
              <Zap size={12} />
            )}
            {isComplete ? t('admin.imagescan.restart') : isPaused ? t('admin.imagescan.resume') : t('admin.imagescan.run_now')}
          </button>
        )}
      </div>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <Camera size={16} className="text-violet-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Bilder funnet</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{formatNumber(stats?.totalWithAutoImage ?? status.totalFound ?? 0)}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Steder med automatisk bilde</p>
        </div>

        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <BarChart3 size={16} className="text-cyan-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Totalt skannet</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{formatNumber(stats?.totalScanned ?? (status.totalFound + status.totalSkipped))}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Behandlet totalt</p>
        </div>

        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Clock size={16} className="text-amber-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Gjenstår</p>
          </div>
          <p className="text-3xl font-black text-[var(--text-primary)]">{formatNumber(status.lastRunRemaining ?? 0)}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">
            {status.lastRunRemaining > 0 && status.lastRunDurationMs && status.lastRunProcessed > 0
              ? `~${Math.ceil(status.lastRunRemaining / status.lastRunProcessed * (status.lastRunDurationMs / 60000))} min estimert`
              : 'Kandidater i kø'}
          </p>
        </div>

        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] p-5 rounded-3xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Zap size={16} className="text-emerald-400" />
            </div>
            <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Sist kjørt</p>
          </div>
          <p className="text-lg font-black text-[var(--text-primary)]">{formatRelativeTime(status.lastRunAt)}</p>
          <p className="text-2xs text-[var(--text-muted)] mt-1">Varighet: {formatDuration(status.lastRunDurationMs)}</p>
        </div>
      </div>

      {/* ── Last Run Details ── */}
      {status.lastRunAt && (
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-6 md:p-8">
          <h3 className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-5">Siste kjøring</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1">Behandlet</p>
              <p className="text-xl font-bold text-[var(--text-primary)]">{status.lastRunProcessed}</p>
            </div>
            <div>
              <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1">Bilde funnet</p>
              <p className="text-xl font-bold text-emerald-400">{status.lastRunFound}</p>
            </div>
            <div>
              <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1">Ingen bilde</p>
              <p className="text-xl font-bold text-[var(--text-muted)]">{status.lastRunSkipped}</p>
            </div>
            <div>
              <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1">Treffratio</p>
              <p className="text-xl font-bold text-violet-400">
                {status.lastRunProcessed > 0 ? `${Math.round(status.lastRunFound / status.lastRunProcessed * 100)}%` : '–'}
              </p>
            </div>
          </div>

          {/* Sources breakdown */}
          {status.lastRunSources && Object.keys(status.lastRunSources).length > 0 && (
            <div className="mt-4 flex items-center gap-4 text-2xs">
              <span className="font-black text-[var(--text-secondary)] uppercase tracking-widest">Kilder:</span>
              {Object.entries(status.lastRunSources).map(([source, count]) => (
                <span key={source} className="px-2 py-0.5 rounded-full bg-[var(--bg-input)] border border-[var(--border-secondary)] text-[var(--text-primary)] font-bold">
                  {source === 'Wikimedia Commons' ? 'Wikimedia' : source} {count}
                </span>
              ))}
            </div>
          )}

          {/* Waypoints */}
          {(status.lastRunWaypointsFound > 0 || status.lastRunWaypointsSkipped > 0) && (
            <div className="mt-3 text-2xs text-[var(--text-muted)]">
              Rutepunkter: {status.lastRunWaypointsFound} bilde funnet, {status.lastRunWaypointsSkipped} uten bilde, {status.lastRunRoutesUpdated} ruter oppdatert
            </div>
          )}
        </div>
      )}

      {/* ── Progress by Spot Type ── */}
      {status.bySpotType && Object.keys(status.bySpotType).length > 0 && (
        <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-6 md:p-8">
          <h3 className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-5">Kandidater per type</h3>
          <div className="space-y-3">
            {Object.entries(status.bySpotType).map(([type, data]) => (
              <div key={type} className="flex items-center gap-4 p-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-primary)]">
                <span className="text-sm font-bold text-[var(--text-primary)] w-40 shrink-0">{t(SPOT_TYPE_LABELS[type] || type)}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-[var(--bg-input)] h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-violet-500 h-full rounded-full transition-all duration-700"
                        style={{ width: data.candidates === 0 ? '100%' : '0%' }}
                      />
                    </div>
                    <span className="text-2xs font-bold text-[var(--text-muted)] whitespace-nowrap">
                      {formatNumber(data.candidates)} gjenstår
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Image Statistics (from getAutoImageStats) ── */}
      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-6 md:p-8">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Bildestatistikk</h3>
          <button
            onClick={loadStats}
            disabled={statsLoading}
            className="text-3xs font-bold text-violet-400 hover:text-violet-300 flex items-center gap-1"
          >
            {statsLoading ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
            Oppdater
          </button>
        </div>

        {stats ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1">Med bilde</p>
                <p className="text-2xl font-black text-emerald-400">{formatNumber(stats.totalWithAutoImage)}</p>
              </div>
              <div className="text-center">
                <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1">Uten bilde</p>
                <p className="text-2xl font-black text-[var(--text-muted)]">{formatNumber(stats.totalAttempted)}</p>
              </div>
              <div className="text-center">
                <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-1">Treffratio</p>
                <p className="text-2xl font-black text-violet-400">
                  {stats.totalScanned > 0 ? `${Math.round(stats.totalWithAutoImage / stats.totalScanned * 100)}%` : '–'}
                </p>
              </div>
            </div>

            {/* By source */}
            {Object.keys(stats.bySource).length > 0 && (
              <div>
                <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">Per kilde</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.bySource).map(([source, count]) => (
                    <div key={source} className="px-3 py-1.5 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-primary)] text-center">
                      <p className="text-2xs text-[var(--text-muted)]">{source === 'Wikimedia Commons' ? 'Wikimedia' : source}</p>
                      <p className="text-sm font-bold text-[var(--text-primary)]">{formatNumber(count as number)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By accuracy */}
            {Object.keys(stats.byAccuracy).length > 0 && (
              <div>
                <p className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest mb-2">Per nøyaktighet</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.byAccuracy).map(([acc, count]) => (
                    <div key={acc} className="px-3 py-1.5 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-primary)] text-center">
                      <p className="text-2xs text-[var(--text-muted)]">{acc === 'ukjent' ? acc : `${acc}m`}</p>
                      <p className="text-sm font-bold text-[var(--text-primary)]">{formatNumber(count as number)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : statsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
          </div>
        ) : (
          <p className="text-xs text-[var(--text-muted)]">Kunne ikke laste statistikk</p>
        )}
      </div>

      {/* ── Control Panel ── */}
      <div className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-6 md:p-8">
        <div className="flex items-center gap-3 mb-5">
          <Settings2 size={14} className="text-[var(--text-secondary)]" />
          <h3 className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">Kontrollpanel</h3>
        </div>

        <div className="flex flex-wrap gap-3">
          {/* Pause / Resume */}
          <button
            onClick={() => handleAction(isPaused ? 'resume' : 'pause')}
            disabled={actionLoading !== null}
            className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${
              isPaused
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-amber-600 hover:bg-amber-500 text-white'
            } ${actionLoading ? 'opacity-50 cursor-wait' : ''}`}
          >
            {actionLoading === 'pause' || actionLoading === 'resume' ? (
              <Loader2 size={12} className="animate-spin" />
            ) : isPaused ? (
              <Play size={12} />
            ) : (
              <Pause size={12} />
            )}
            {isPaused ? t('admin.imagescan.resume') : t('admin.imagescan.pause')}
          </button>

          {/* Reset */}
          {confirmReset ? (
            <div className="flex items-center gap-2">
              <span className="text-2xs text-red-400 font-bold">Nullstill alle attempted-flagg?</span>
              <button
                onClick={() => handleAction('reset')}
                disabled={actionLoading !== null}
                className="px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-red-600 hover:bg-red-500 text-white transition-all"
              >
                {actionLoading === 'reset' ? <Loader2 size={12} className="animate-spin" /> : t('admin.imagescan.confirm')}
              </button>
              <button
                onClick={() => setConfirmReset(false)}
                className="px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-input)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all"
              >
                Avbryt
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmReset(true)}
              disabled={actionLoading !== null || isResetting || isResetPending}
              className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all bg-red-600/80 hover:bg-red-500 text-white ${
                actionLoading || isResetting || isResetPending ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <RotateCcw size={12} />
              Nullstill skanning
            </button>
          )}

          {/* Manual backfill */}
          <button
            onClick={handleManualBackfill}
            disabled={actionLoading !== null}
            className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all bg-cyan-600 hover:bg-cyan-500 text-white ${
              actionLoading ? 'opacity-50 cursor-wait' : ''
            }`}
          >
            {actionLoading === 'backfill' ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
            Kjør manuell batch
          </button>

          {/* Restart full scan */}
          <button
            onClick={handleRestartScan}
            disabled={actionLoading !== null}
            className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all bg-violet-600 hover:bg-violet-500 text-white ${
              actionLoading ? 'opacity-50 cursor-wait' : ''
            }`}
          >
            {actionLoading === 'restart' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Start skanning på nytt
          </button>
        </div>

        {/* Backfill result */}
        {backfillResult && (
          <div className="mt-4 p-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--border-primary)] text-2xs">
            {backfillResult.error ? (
              <span className="text-red-400">Feil: {backfillResult.error}</span>
            ) : (
              <span className="text-[var(--text-primary)]">
                Behandlet {backfillResult.processed} steder: {backfillResult.found} bilde funnet, {backfillResult.skipped} uten bilde, {backfillResult.remaining} gjenstår
              </span>
            )}
          </div>
        )}

        {/* Last reset info */}
        {status.lastResetAt && (
          <p className="mt-3 text-2xs text-[var(--text-muted)]">
            Sist nullstilt: {formatDate(status.lastResetAt)} — {formatNumber(status.lastResetCount ?? 0)} steder tilbakestilt
          </p>
        )}
      </div>

      {/* ── Last updated ── */}
      {status.lastCompletedAt && (
        <p className="text-2xs text-[var(--text-muted)] text-center">
          Sist oppdatert: {formatDate(status.lastCompletedAt)}
        </p>
      )}
    </div>
  );
};

export default AdminImageScanDashboard;
